import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaTimes, FaUndo } from "react-icons/fa";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  notify,
  RefreshButton,
  Select,
  Table,
  Text,
  VStack,
} from "../../../components/ui";
import {
  MaskingRuleDto,
  approveSuggestedRules,
  declineSuggestedRules,
  getSuggestedMaskingRules,
  restoreSuggestedRules,
} from "../../../services/maskingService";
import {
  AcceptSuggestionRequest,
  SUGGESTION_KINDS,
  Suggestion,
  SuggestionKind,
  SuggestionStatus,
  suggestionService,
} from "../../../services/suggestionService";
import { useSuggestions } from "../../../contexts/SuggestionsContext";
import SkillDraftDialog from "./components/SkillDraftDialog";
import SuggestionGroup from "./components/SuggestionGroup";

type MaskingStatusFilter = "pending" | "dismissed";

/**
 * Everything the product has proposed to this tenant, in one place — including suggestions that were
 * already dismissed, so a mis-click is recoverable rather than permanent.
 *
 * The analytics suggestions (shortcuts, skills, amendments, toolkit changes, knowledge gaps, schedules,
 * defects) are grouped by the NEED they answer, so an admin sees "users keep failing at X" once with
 * every remedy under it. Masking suggestions stay a separate table: they come from a different
 * detector and own their own fetch, filter and empty state so one failing call never blanks the other.
 */
const SuggestionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { refreshCounts } = useSuggestions();

  const [suggestionStatus, setSuggestionStatus] =
    useState<SuggestionStatus>("Pending");
  const [kindFilter, setKindFilter] = useState<SuggestionKind | "all">("all");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [skillDialog, setSkillDialog] = useState<Suggestion | null>(null);
  // A shortcut accepted together with its skill: accept the skill first, then the chip.
  const [pendingPair, setPendingPair] = useState<Suggestion | null>(null);

  const [maskingStatus, setMaskingStatus] =
    useState<MaskingStatusFilter>("pending");
  const [maskingRules, setMaskingRules] = useState<MaskingRuleDto[]>([]);
  const [maskingLoading, setMaskingLoading] = useState(true);

  const loadSuggestions = useCallback(
    async (status: SuggestionStatus) => {
      setSuggestionsLoading(true);
      try {
        setSuggestions(await suggestionService.list(undefined, status));
      } catch (error: any) {
        notify({
          type: "error",
          title: t("app.suggestions.errors.loadShortcutsFailed"),
          description: error?.response?.data?.message ?? error?.message,
        });
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [t],
  );

  const loadMasking = useCallback(
    async (status: MaskingStatusFilter) => {
      setMaskingLoading(true);
      try {
        // Dismissed rules only come back when explicitly asked for; filter client-side to the
        // bucket the admin picked.
        const rules = await getSuggestedMaskingRules(undefined, true);
        setMaskingRules(
          rules.filter((r) =>
            status === "dismissed" ? !!r.dismissedAt : !r.dismissedAt,
          ),
        );
      } catch (error: any) {
        notify({
          type: "error",
          title: t("app.suggestions.errors.loadMaskingFailed"),
          description: error?.response?.data?.message ?? error?.message,
        });
      } finally {
        setMaskingLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    loadSuggestions(suggestionStatus);
  }, [loadSuggestions, suggestionStatus]);

  useEffect(() => {
    loadMasking(maskingStatus);
  }, [loadMasking, maskingStatus]);

  // ── Suggestion actions ──────────────────────────────────────────────────

  const runSuggestionAction = async (
    action: () => Promise<unknown>,
    successKey: string,
    errorKey: string,
  ) => {
    try {
      await action();
      notify({ type: "success", title: t(successKey) });
      await loadSuggestions(suggestionStatus);
      refreshCounts();
    } catch (error: any) {
      notify({
        type: "error",
        title: t(errorKey),
        description: error?.response?.data?.message ?? error?.message,
      });
      throw error;
    }
  };

  const acceptedToastKey = (kind: SuggestionKind): string =>
    `app.suggestions.acceptedToast.${kind}`;

  /** Skill kinds open the review dialog; everything else accepts directly. */
  const handleAccept = (s: Suggestion): void => {
    if (s.kind === "Skill" || s.kind === "SkillAmendment") {
      setPendingPair(null);
      setSkillDialog(s);
      return;
    }
    void runSuggestionAction(
      () => suggestionService.accept(s.id),
      acceptedToastKey(s.kind),
      "app.suggestions.errors.acceptFailed",
    ).catch(() => undefined);
  };

  const handleAcceptBoth = (shortcut: Suggestion, skill: Suggestion): void => {
    setPendingPair(shortcut);
    setSkillDialog(skill);
  };

  const handleSkillConfirm = async (body: AcceptSuggestionRequest) => {
    const skill = skillDialog;
    if (!skill) return;
    const pair = pendingPair;
    try {
      await runSuggestionAction(
        async () => {
          await suggestionService.accept(skill.id, body);
          if (pair) await suggestionService.accept(pair.id);
        },
        acceptedToastKey(skill.kind),
        "app.suggestions.errors.acceptFailed",
      );
      setSkillDialog(null);
      setPendingPair(null);
    } catch {
      /* toast already shown; keep the dialog open so edits are not lost */
    }
  };

  const handleDismiss = (s: Suggestion): void => {
    void runSuggestionAction(
      () => suggestionService.dismiss(s.id),
      "app.suggestions.shortcuts.dismissedToast",
      "app.suggestions.errors.dismissFailed",
    ).catch(() => undefined);
  };

  const handleRestore = (s: Suggestion): void => {
    void runSuggestionAction(
      () => suggestionService.restore(s.id),
      "app.suggestions.shortcuts.restoredToast",
      "app.suggestions.errors.restoreFailed",
    ).catch(() => undefined);
  };

  const handleDisableTool = (s: Suggestion): void => {
    void runSuggestionAction(
      () => suggestionService.accept(s.id, { disableTool: true }),
      "app.suggestions.acceptedToast.Defect",
      "app.suggestions.errors.acceptFailed",
    ).catch(() => undefined);
  };

  /**
   * One block, every pending line, as drafted — the preview on each line is what was agreed to.
   * A defect line is accepted as "noted" (the tool stays on); Disable tool remains a per-line choice.
   */
  const handleAcceptAll = (rows: Suggestion[]): void => {
    void runSuggestionAction(
      async () => {
        for (const row of rows) await suggestionService.accept(row.id);
      },
      "app.suggestions.group.acceptedAllToast",
      "app.suggestions.errors.acceptFailed",
    ).catch(() => undefined);
  };

  const handleDismissAll = (rows: Suggestion[]): void => {
    void runSuggestionAction(
      async () => {
        for (const row of rows) await suggestionService.dismiss(row.id);
      },
      "app.suggestions.group.dismissedAllToast",
      "app.suggestions.errors.dismissFailed",
    ).catch(() => undefined);
  };

  const visible = suggestions.filter(
    (s) => kindFilter === "all" || s.kind === kindFilter,
  );
  const defects = visible.filter((s) => s.kind === "Defect");
  const groups = Object.values(
    visible
      .filter((s) => s.kind !== "Defect")
      .reduce<Record<string, Suggestion[]>>((acc, s) => {
        (acc[s.clusterHash] ??= []).push(s);
        return acc;
      }, {}),
  ).sort(
    (a, b) =>
      Math.max(...b.map((s) => s.score)) - Math.max(...a.map((s) => s.score)),
  );

  // ── Masking actions ─────────────────────────────────────────────────────

  const runMaskingAction = async (
    action: () => Promise<unknown>,
    successKey: string,
    errorKey: string,
  ) => {
    try {
      await action();
      notify({ type: "success", title: t(successKey) });
      await loadMasking(maskingStatus);
      refreshCounts();
    } catch (error: any) {
      notify({
        type: "error",
        title: t(errorKey),
        description: error?.response?.data?.message ?? error?.message,
      });
    }
  };

  const suggestionStatusItems = [
    { value: "Pending", label: t("app.suggestions.status.pending") },
    { value: "Dismissed", label: t("app.suggestions.status.dismissed") },
    { value: "Accepted", label: t("app.suggestions.status.accepted") },
  ];

  const kindItems = [
    { value: "all", label: t("app.suggestions.kinds.all") },
    ...SUGGESTION_KINDS.map((k) => ({
      value: k,
      label: t(`app.suggestions.kinds.${k}`),
    })),
  ];

  const maskingStatusItems = [
    { value: "pending", label: t("app.suggestions.status.pending") },
    { value: "dismissed", label: t("app.suggestions.status.dismissed") },
  ];

  return (
    <Box p={6}>
      <VStack align="stretch" gap={6}>
        <Box>
          <Heading size="lg">{t("app.suggestions.title")}</Heading>
          <Text color="var(--text-secondary-color)" mt={1}>
            {t("app.suggestions.subtitle")}
          </Text>
        </Box>

        {/* ── Suggestions (all kinds, grouped by need) ─────────────────── */}
        <VStack align="stretch" gap={3}>
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <Heading size="md">{t("app.suggestions.needs.heading")}</Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)" mt={1}>
                {t("app.suggestions.needs.description")}
              </Text>
            </Box>
            <HStack gap={2}>
              <Select
                size="sm"
                w="180px"
                items={kindItems}
                value={[kindFilter]}
                onValueChange={(d) =>
                  setKindFilter(d.value[0] as SuggestionKind | "all")
                }
              />
              <Select
                size="sm"
                w="160px"
                items={suggestionStatusItems}
                value={[suggestionStatus]}
                onValueChange={(d) =>
                  setSuggestionStatus(d.value[0] as SuggestionStatus)
                }
              />
              <RefreshButton
                onClick={() => loadSuggestions(suggestionStatus)}
                loading={suggestionsLoading}
              />
            </HStack>
          </HStack>

          {suggestionsLoading ? (
            <Text>{t("app.common.loading")}</Text>
          ) : visible.length === 0 ? (
            <Text color="var(--text-secondary-color)">
              {t("app.suggestions.needs.empty")}
            </Text>
          ) : (
            <VStack align="stretch" gap={2}>
              {defects.length > 0 && (
                <Box>
                  <Heading size="sm" color="var(--text-primary)">
                    {t("app.suggestions.defects.heading")}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color="var(--text-secondary-color)"
                    mb={2}
                  >
                    {t("app.suggestions.defects.description")}
                  </Text>
                  <VStack align="stretch" gap={2}>
                    {Object.values(
                      defects.reduce<Record<string, Suggestion[]>>((acc, s) => {
                        (acc[s.clusterHash] ??= []).push(s);
                        return acc;
                      }, {}),
                    ).map((rows) => (
                      <SuggestionGroup
                        key={rows[0].clusterHash}
                        rows={rows}
                        onAccept={handleAccept}
                        onAcceptBoth={handleAcceptBoth}
                        onDismiss={handleDismiss}
                        onRestore={handleRestore}
                        onDisableTool={handleDisableTool}
                        onAcceptAll={handleAcceptAll}
                        onDismissAll={handleDismissAll}
                      />
                    ))}
                  </VStack>
                </Box>
              )}
              {groups.map((rows) => (
                <SuggestionGroup
                  key={rows[0].clusterHash}
                  rows={rows}
                  onAccept={handleAccept}
                  onAcceptBoth={handleAcceptBoth}
                  onDismiss={handleDismiss}
                  onRestore={handleRestore}
                  onDisableTool={handleDisableTool}
                  onAcceptAll={handleAcceptAll}
                  onDismissAll={handleDismissAll}
                />
              ))}
            </VStack>
          )}
        </VStack>

        <SkillDraftDialog
          suggestion={skillDialog}
          onClose={() => {
            setSkillDialog(null);
            setPendingPair(null);
          }}
          onConfirm={handleSkillConfirm}
        />

        {/* ── Masking rule suggestions ─────────────────────────────────── */}
        <VStack align="stretch" gap={3}>
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <Heading size="md">
                {t("app.suggestions.masking.heading")}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)" mt={1}>
                {t("app.suggestions.masking.description")}
              </Text>
            </Box>
            <HStack gap={2}>
              <Select
                size="sm"
                w="180px"
                items={maskingStatusItems}
                value={[maskingStatus]}
                onValueChange={(d) =>
                  setMaskingStatus(d.value[0] as MaskingStatusFilter)
                }
              />
              <RefreshButton
                onClick={() => loadMasking(maskingStatus)}
                loading={maskingLoading}
              />
            </HStack>
          </HStack>

          <Box borderRadius="md" overflow="hidden" overflowX="auto">
            <Table.Root variant="outline" size="sm" minW="640px">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t("app.suggestions.masking.table.rule")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.suggestions.masking.table.fieldPath")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="180px">
                    {t("app.suggestions.masking.table.toolkit")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="190px">
                    {t("app.suggestions.table.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {maskingLoading ? (
                  <Table.Row>
                    <Table.Cell colSpan={4} textAlign="center">
                      <Text>{t("app.common.loading")}</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : maskingRules.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={4} textAlign="center">
                      <Text color="var(--text-secondary-color)">
                        {t("app.suggestions.masking.empty")}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  maskingRules.map((rule) => (
                    <Table.Row key={rule.id}>
                      <Table.Cell>
                        <VStack align="start" gap={0}>
                          <Text fontWeight="medium">{rule.name}</Text>
                          {rule.description && (
                            <Text
                              fontSize="xs"
                              color="var(--text-secondary-color)"
                            >
                              {rule.description}
                            </Text>
                          )}
                        </VStack>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" fontFamily="mono">
                          {rule.fieldPath}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs">
                          {rule.serverName ??
                            t("app.suggestions.masking.allToolkits")}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={2} flexWrap="wrap">
                          <Button
                            size="xs"
                            variant="outline"
                            colorPalette="blue"
                            color="colorPalette.fg"
                            borderColor="colorPalette.solid"
                            _hover={{
                              bg: "colorPalette.subtle",
                              borderColor: "colorPalette.solid",
                            }}
                            onClick={() =>
                              runMaskingAction(
                                () => approveSuggestedRules([rule.id]),
                                "app.suggestions.masking.approvedToast",
                                "app.suggestions.errors.acceptFailed",
                              )
                            }
                          >
                            <FaCheck /> {t("app.suggestions.approve")}
                          </Button>
                          {rule.dismissedAt ? (
                            <Button
                              size="xs"
                              intent="secondary"
                              onClick={() =>
                                runMaskingAction(
                                  () => restoreSuggestedRules([rule.id]),
                                  "app.suggestions.masking.restoredToast",
                                  "app.suggestions.errors.restoreFailed",
                                )
                              }
                            >
                              <FaUndo /> {t("app.suggestions.restore")}
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              intent="secondary"
                              onClick={() =>
                                runMaskingAction(
                                  () => declineSuggestedRules([rule.id]),
                                  "app.suggestions.masking.dismissedToast",
                                  "app.suggestions.errors.dismissFailed",
                                )
                              }
                            >
                              <FaTimes /> {t("app.suggestions.dismiss")}
                            </Button>
                          )}
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Box>
        </VStack>
      </VStack>
    </Box>
  );
};

export default SuggestionsPage;
